// One owner for every Printd instance in this app, because Printd has a
// constructor that touches the DOM and no teardown of its own.
//
// The defect this exists to end (ai_docs/perf/web_memory_investigation.md,
// finding F1): `new Printd()` appends a hidden <iframe> to document.body in its
// constructor (`createIFrame`, printd/index.js) and offers nothing that removes
// it. Nine pages called `new Printd()` from `mounted()`, so every in-SPA visit
// to Dispatch leaked two iframes and every visit to a load page leaked one
// more; three of those sites additionally called `new Printd()` inside the
// print click handler, leaking one per print. Each orphaned iframe is a live
// browsing context — its own Window, NativeContext and HTMLDocument — held by a
// GC root, so forced garbage collection never reclaims one. Measured on the
// production build over 40 board -> load -> board round trips: +2.93 Documents
// and +1.054 MB of JS heap per cycle, linear, no plateau (R^2 = 0.9996).
//
// The mechanism chosen, of the two the report offered:
//
//   1. LAZY construction. Nothing is created until something actually prints,
//      so a page that is opened and never printed from now costs zero iframes.
//      That alone removes the entire navigation-cycle leak, because the leak
//      was paid at mount by pages nobody prints from.
//   2. ONE instance per owner, reused across prints. This is what bounds the
//      per-click sites: printing ten times from a batch list creates one
//      iframe, not ten.
//   3. Explicit teardown on destroy, which removes the iframe from the DOM.
//      After `destroy()` the owner is back to state 1 — a later print (there is
//      none, the component is gone) would build a fresh instance.
//
// Deliberately NOT per-print teardown, which the brief left open. Printd's
// print cycle is asynchronous: `print()` writes the document and then waits for
// the iframe's `load` event before invoking the caller's callback and calling
// `contentWindow.print()`. Removing the iframe when `print()` returns would
// abort the print; removing it after `launchPrint()` returns would depend on
// `window.print()` blocking until the print dialog closes, which Chrome happens
// to do and the HTML spec does not require. Holding one iframe per live
// printing page is bounded, which is all the leak fix needs, and it keeps the
// print path byte-identical to the one that shipped.
import { createApp } from 'vue';
import { Printd } from 'printd';

// The bare printer, with no framework in it — this is the unit the tests drive.
// `printerMixin` below is the thing components use.
export function createPrinter(options) {
  let instance = null;

  return {
    // Same signature Printd.print has, forwarded verbatim (element, styles,
    // scripts, callback), so call sites read the same as before and Printd's
    // callback chain — callback({ iframe, element, launchPrint }) — is
    // untouched.
    print(el, styles, scripts, callback) {
      if (!instance) instance = new Printd(options);
      instance.print(el, styles, scripts, callback);
      return instance;
    },

    // Removes the hidden iframe from document.body and drops the instance.
    // Defensive about `getIFrame` because the existing page tests mock the
    // whole `printd` module with a bare `{ print }` object; a teardown that
    // threw there would turn a memory fix into nine broken test files.
    destroy() {
      if (!instance) return;
      const frame = typeof instance.getIFrame === 'function' ? instance.getIFrame() : null;
      if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
      instance = null;
    },

    // Test seam: null until the first print. Nothing in the app reads this.
    get instance() {
      return instance;
    },
  };
}

// The mixin every printing page uses. It puts the teardown in one place rather
// than in nine hand-written `beforeUnmount` hooks that can drift apart (or be
// dropped by the next person who copies one of these pages), and Vue merges
// mixin lifecycle hooks with the component's own, so pages that already have a
// `beforeUnmount` keep it and get this one too.
//
// `$printer` is assigned as a plain instance property, not data: putting a
// Printd instance in `data()` would make Vue walk its internals installing
// reactive getters on an iframe reference.
export const printerMixin = {
  created() {
    this.$printer = createPrinter();
  },
  beforeUnmount() {
    if (this.$printer) this.$printer.destroy();
  },
};

// The construct-mount-print contract every print surface uses, in one place.
//
// Under Vue 2 each page did `const Sheet = Vue.extend(sheet); const content =
// new Sheet({ propsData }); content.$mount();` and handed `content.$el` and
// `content.cssText` to the printer. Vue 3 has neither `Vue.extend` nor a
// detached `$mount`; the equivalent is a throwaway application mounted into an
// element that is never attached to the document. `store` is optional and
// installed when given, so a sheet that reads `this.$store` keeps working; the
// sheets that `require()` the store module directly need nothing.
//
// Returns the mounted root instance (so `.$el` and `.cssText` read exactly as
// before) plus an `unmount` for callers that want to release it. Nothing today
// calls unmount, matching the Vue 2 sites, which never called $destroy either.
export function renderSheet(component, props, { store } = {}) {
  const host = document.createElement('div');
  const app = createApp(component, props);
  if (store) app.use(store);
  const vm = app.mount(host);
  return { el: vm.$el, cssText: vm.cssText, vm, unmount: () => app.unmount() };
}

export default printerMixin;
